<script setup lang="ts">
import { ChevronDown as ChevronDownIcon } from '@lucide/vue'
import { computed, ref } from 'vue'

import { useEventListener } from '@/composables/useEventListener'
import type { TrackProfileMetricId } from '@/utils/trackProfileAnalysis'

defineOptions({ name: 'MetricSelector' })

export interface MetricOption {
  id: TrackProfileMetricId
  label: string
}

const props = withDefaults(
  defineProps<{
    modelValue: TrackProfileMetricId
    options?: MetricOption[]
    disabled?: boolean
    /** Which edge the dropdown menu aligns to (match the button's position). */
    align?: 'left' | 'right'
  }>(),
  {
    options: () => [
      { id: 'gradient', label: 'Elevation' },
      { id: 'surface', label: 'Surface' },
      { id: 'wayType', label: 'Way type' },
    ],
    disabled: false,
    align: 'right',
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: TrackProfileMetricId]
}>()

const isOpen = ref(false)
const rootRef = ref<HTMLElement | null>(null)

const currentLabel = computed(
  () =>
    props.options.find((option) => option.id === props.modelValue)?.label ??
    props.options[0]?.label ??
    '',
)

const toggle = () => {
  if (props.disabled) return
  isOpen.value = !isOpen.value
}

const select = (id: TrackProfileMetricId) => {
  isOpen.value = false
  if (id !== props.modelValue) emit('update:modelValue', id)
}

useEventListener(document, 'click', (event) => {
  if (!isOpen.value) return
  if (rootRef.value && !rootRef.value.contains(event.target as Node)) {
    isOpen.value = false
  }
})
useEventListener(document, 'keydown', (event: KeyboardEvent) => {
  if (isOpen.value && event.key === 'Escape') isOpen.value = false
})
</script>

<template>
  <div ref="rootRef" class="relative inline-block text-left">
    <button
      type="button"
      class="-mx-1 inline-flex items-center gap-1 rounded-md px-1 py-1 text-base font-semibold text-neutral-900 transition-colors hover:bg-black/5 disabled:opacity-50 hover:cursor-pointer"
      :disabled="disabled"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      aria-label="Colour the elevation chart by"
      data-testid="metric-selector-button"
      @click.stop="toggle"
    >
      <span>{{ currentLabel }}</span>
      <ChevronDownIcon class="h-4 w-4 text-neutral-400" />
    </button>

    <ul
      v-if="isOpen"
      class="absolute z-20 mt-1 min-w-36 overflow-hidden rounded-xl border border-black/10 bg-white/95 py-1 shadow-lg backdrop-blur-xl"
      :class="align === 'left' ? 'left-0' : 'right-0'"
      role="listbox"
      data-testid="metric-selector-list"
    >
      <li
        v-for="option in options"
        :key="option.id"
        role="option"
        :aria-selected="option.id === modelValue"
      >
        <button
          type="button"
          class="flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors hover:bg-black/5 hover:cursor-pointer"
          :class="option.id === modelValue ? 'font-semibold text-emerald-700' : 'text-neutral-700'"
          @click.stop="select(option.id)"
        >
          {{ option.label }}
        </button>
      </li>
    </ul>
  </div>
</template>
